import { put, select, takeLatest } from 'redux-saga/effects';
import { getExtendedProductScopeUsOrGlobal } from '@discover-frontend/utils';
import {
  GET_COMPANIES_REQUEST,
  GET_COMPANIES_SUCCESS,
  GET_COMPANIES_FAILURE,
  GET_USERS_REQUEST,
  GET_USERS_SUCCESS,
  GET_USERS_FAILURE,
  GET_USERS_BY_COMPANY_REQUEST,
  GET_USERS_BY_COMPANY_SUCCESS,
  GET_USERS_BY_COMPANY_FAILURE,
  GET_FUSIONS_REQUEST,
  GET_AUDIENCE_BUILDERS_REQUEST,
  GET_FUSIONS_SUCCESS,
  GET_FUSIONS_FAILURE,
  GET_AUDIENCE_BUILDERS_SUCCESS,
  GET_AUDIENCE_BUILDERS_FAILURE,
  GET_MY_COMPANY_SUCCESS,
  GET_MY_COMPANY_FAILURE,
  GET_MY_COMPANY_REQUEST,
  IGNORE_TWITTER_TOKEN_REQUEST,
  IGNORE_TWITTER_TOKEN_SUCCESS,
  IGNORE_TWITTER_TOKEN_FAILURE,
  GENERATE_TWITTER_URL_SUCCESS,
  GENERATE_TWITTER_URL_REQUEST,
  GENERATE_TWITTER_URL_FAILURE,
  REFRESH_ME,
  TWITTER_CALLBACK_URL_STATUS_SUCCESS,
  UPDATE_TABS_FROM_COMPANY_SETTINGS,
} from '../constants/action-types';
import * as accountsApi from '../services/accountsApi';

import sagaFetch from './SagaFetch';
import {
  alphabeticallySortByKey,
  getAudienceBuildersIdsAndSourceIdsFromQuery,
} from '../utils/misc';
import { getCompanyIdFromToken, getMyId } from '../utils/AuthHelperMethods';
import {
  addToast,
  closeTwitterAuthorizationModal,
  getDemographics,
  getDemographicsSearchResults,
  getTaxonomyList,
} from '../actions';
import translations from '../lang';
import * as api from '../services/meApi';
import { getCurrentProductDataSources } from '../selectors/myCompany';

function* fetchCompanies(action) {
  try {
    const fusions = yield select((state) => state.fusions.data);
    const audienceBuilders = yield select(
      (state) => state.audienceBuilders.data
    );
    const audienceBuildersIdsAndSourceIds =
      getAudienceBuildersIdsAndSourceIdsFromQuery(
        action.query,
        fusions,
        audienceBuilders
      );
    const queryStringArr = [];

    queryStringArr.push(`internal=true`);
    queryStringArr.push(`status=active`);
    queryStringArr.push(`fields=users_count`);

    if (
      audienceBuildersIdsAndSourceIds.audience_builder_ids &&
      audienceBuildersIdsAndSourceIds.audience_builder_ids.length > 0
    ) {
      queryStringArr.push(
        `audience_builders=${audienceBuildersIdsAndSourceIds.audience_builder_ids.join(
          ','
        )}`
      );
    }
    const queryString = `?${queryStringArr.join('&')}`;
    const { response, error } = yield sagaFetch(
      accountsApi.fetchCompaniesApi,
      queryString
    );

    if (!error) {
      return yield put({
        type: GET_COMPANIES_SUCCESS,
        response: response.data,
      });
    }
    return yield put({ type: GET_COMPANIES_FAILURE, error });
  } catch (error) {
    return yield put({ type: GET_COMPANIES_FAILURE, error });
  }
}

function* fetchUsers(action) {
  try {
  const queryStringArr = [];
  const fusions = yield select((state) => state.fusions.data);
  const audienceBuilders = yield select((state) => state.audienceBuilders.data);
  const audienceBuildersIdsAndSourceIds =
    getAudienceBuildersIdsAndSourceIdsFromQuery(
      action.query,
      fusions,
      audienceBuilders
    );
  queryStringArr.push(`status=active`);
  queryStringArr.push(`sort_by=name`);

  if (
    audienceBuildersIdsAndSourceIds.audience_builder_ids &&
    audienceBuildersIdsAndSourceIds.audience_builder_ids.length > 0
  ) {
    queryStringArr.push(
      `audience_builder_ids=${audienceBuildersIdsAndSourceIds.audience_builder_ids.join(
        ','
      )}`
    );
  }

  if (
    audienceBuildersIdsAndSourceIds.source_ids &&
    audienceBuildersIdsAndSourceIds.source_ids.length > 0
  ) {
    queryStringArr.push(
      `source_ids=${audienceBuildersIdsAndSourceIds.source_ids.join(',')}`
    );
  }

  const queryString = `?${queryStringArr.join('&')}`;
  const queryStringForCount = `${queryString}&limit=1`;
  const responseForCount = yield sagaFetch(
    accountsApi.fetchUsersApi,
    queryStringForCount
  );

  if (responseForCount.error || !responseForCount.response) {
    return yield put({ type: GET_USERS_FAILURE, error: responseForCount.error });
  }

  const totalCount = responseForCount.response.total_count ?? 0;
  if (totalCount === 0) {
    return yield put({ type: GET_USERS_SUCCESS, response: [] });
  }

  const actualQueryString = `${queryString}&limit=${totalCount}`;
  const { response, error } = yield sagaFetch(
    accountsApi.fetchUsersApi,
    actualQueryString
  );

  if (!error) {
    const users = Array.isArray(response.data)
      ? response.data.filter((user) => user.id !== Number(getMyId()))
      : [];
    return yield put({
      type: GET_USERS_SUCCESS,
      response: users,
    });
  }
  return yield put({ type: GET_USERS_FAILURE, error });
  } catch (error) {
    return yield put({ type: GET_USERS_FAILURE, error });
  }
}

function* fetchUsersByCompany(action) {
  try {
  const queryStringArr = [];
  const fusions = yield select((state) => state.fusions.data);
  const audienceBuilders = yield select((state) => state.audienceBuilders.data);
  const audienceBuildersIdsAndSourceIds =
    getAudienceBuildersIdsAndSourceIdsFromQuery(
      action.query,
      fusions,
      audienceBuilders
    );
  queryStringArr.push(`status=active`);

  if (
    audienceBuildersIdsAndSourceIds.source_ids &&
    audienceBuildersIdsAndSourceIds.source_ids.length > 0
  ) {
    queryStringArr.push(
      `source_ids=${audienceBuildersIdsAndSourceIds.source_ids.join(',')}`
    );
  }

  const queryString = `?${queryStringArr.join('&')}`;
  const queryStringForCount = `${queryString}&limit=1`;
  const responseForCount = yield sagaFetch(
    accountsApi.fetchCompanyUsersApi,
    action.id,
    queryStringForCount
  );

  if (responseForCount.error || !responseForCount.response) {
    return yield put({
      type: GET_USERS_BY_COMPANY_FAILURE,
      error: responseForCount.error,
    });
  }

  const totalCount = responseForCount.response.total_count ?? 0;
  if (totalCount === 0) {
    return yield put({
      type: GET_USERS_BY_COMPANY_SUCCESS,
      response: [],
    });
  }

  const actualQueryString = `${queryString}&limit=${totalCount}`;
  const { response, error } = yield sagaFetch(
    accountsApi.fetchCompanyUsersApi,
    action.id,
    actualQueryString
  );

  if (!error) {
    const users = Array.isArray(response.data)
      ? response.data
          .filter((user) => user.id !== Number(getMyId()))
          .sort(alphabeticallySortByKey('name'))
      : [];
    return yield put({
      type: GET_USERS_BY_COMPANY_SUCCESS,
      response: users,
    });
  }
  return yield put({ type: GET_USERS_BY_COMPANY_FAILURE, error });
  } catch (error) {
    return yield put({ type: GET_USERS_BY_COMPANY_FAILURE, error });
  }
}

function* fetchFusions() {
  const meProducts = yield select((state) => state.meProducts);
  const product =
    meProducts.data &&
    meProducts.data.find(
      (product) => product.slug === getExtendedProductScopeUsOrGlobal()
    );
  let queryString = '';

  if (product) {
    queryString = `?products=${product.id}`;
  }
  const { response, error } = yield sagaFetch(
    accountsApi.fetchFusionsApi,
    queryString
  );

  if (response) {
    return yield put({ type: GET_FUSIONS_SUCCESS, response: response.data });
  }
  return yield put({ type: GET_FUSIONS_FAILURE, error });
}

function* fetchAudienceBuilders() {
  const meProducts = yield select((state) => state.meProducts);
  const product =
    meProducts.data &&
    meProducts.data.find(
      (product) => product.slug === getExtendedProductScopeUsOrGlobal()
    );
  let queryString = '';

  if (product) {
    queryString = `?products=${product.id}`;
  }

  const { response, error } = yield sagaFetch(
    accountsApi.fetchAudienceBuildersApi,
    queryString
  );
  if (response) {
    return yield put({
      type: GET_AUDIENCE_BUILDERS_SUCCESS,
      response: response.data,
    });
  }
  return yield put({ type: GET_AUDIENCE_BUILDERS_FAILURE, error });
}

function* fetchMyCompany() {
  const { response, error } = yield sagaFetch(
    accountsApi.fetchCompanyApi,
    getCompanyIdFromToken()
  );

  if (!error) {
    return yield put({ type: GET_MY_COMPANY_SUCCESS, response: response.data });
  }
  return yield put({ type: GET_MY_COMPANY_FAILURE, error });
}

function* handleMyCompanySuccess() {
  const productDataSources = yield select(getCurrentProductDataSources);

  yield put({
    type: UPDATE_TABS_FROM_COMPANY_SETTINGS,
    sources: [...productDataSources, 'twitter'],
  });

  yield put(getTaxonomyList());
  yield put(getDemographics());

  yield put(getDemographicsSearchResults('', 'metro_area'));
  yield put(getDemographicsSearchResults('', 'job_function'));
  yield put(getDemographicsSearchResults('', 'language'));
}

function* fetchIgnoreTokenRequest() {
  const { response, error } = yield sagaFetch(
    accountsApi.fetchIgnoreTwitterTokenApi
  );

  if (!error) {
    yield put(
      addToast({
        status: 'success',
        message: translations['en-US'].TOASTS.IGNORE_TWITTER_TOKEN,
      })
    );
    yield put(closeTwitterAuthorizationModal());
    return yield put({
      type: IGNORE_TWITTER_TOKEN_SUCCESS,
      response: response.data,
    });
  }
  yield put(
    addToast({
      status: 'error',
      message: translations['en-US'].TOASTS.IGNORE_TWITTER_TOKEN_ERROR,
    })
  );
  return yield put({ type: IGNORE_TWITTER_TOKEN_FAILURE, error });
}

function* fetchGenerateTwitterUrlRequest() {
  const { response, error } = yield sagaFetch(
    accountsApi.fetchGenerateTwitterUrlApi
  );

  if (!error) {
    const url = response.redirectUrl;
    const oAuthToken = response.data && response.data.oauth_token;
    if (url && oAuthToken) {
      const twitterAuthWindow = window.open(
        url,
        '_blank',
        'resizable,scrollbars,status'
      );

      yield put(
        addToast({
          status: 'success',
          message: translations['en-US'].TOASTS.GENERATE_TWITTER_URL,
        })
      );
      return yield put({
        type: GENERATE_TWITTER_URL_SUCCESS,
        oAuthToken,
        twitterAuthWindow,
      });
    }
  }
  yield put(
    addToast({
      status: 'error',
      message: translations['en-US'].TOASTS.GENERATE_TWITTER_URL_ERROR,
    })
  );
  return yield put({ type: GENERATE_TWITTER_URL_FAILURE, error });
}

function* handleTwitterCallbackUrlStatusSuccess(action) {
  const me = yield select((state) => state.me);
  const { twitterAuthWindow } = me;
  if (twitterAuthWindow) {
    twitterAuthWindow.close();
  }
  yield put(closeTwitterAuthorizationModal());
  const { response } = yield sagaFetch(api.fetchMeApi);

  if (response) {
    return yield put({ type: REFRESH_ME, response: response.data });
  }
}

export function* accountsActionWatcher() {
  yield takeLatest(GET_COMPANIES_REQUEST, fetchCompanies);
  yield takeLatest(GET_USERS_REQUEST, fetchUsers);
  yield takeLatest(GET_USERS_BY_COMPANY_REQUEST, fetchUsersByCompany);
  yield takeLatest(GET_FUSIONS_REQUEST, fetchFusions);
  yield takeLatest(GET_AUDIENCE_BUILDERS_REQUEST, fetchAudienceBuilders);
  yield takeLatest(GET_MY_COMPANY_REQUEST, fetchMyCompany);
  yield takeLatest(GET_MY_COMPANY_SUCCESS, handleMyCompanySuccess);
  yield takeLatest(IGNORE_TWITTER_TOKEN_REQUEST, fetchIgnoreTokenRequest);
  yield takeLatest(
    GENERATE_TWITTER_URL_REQUEST,
    fetchGenerateTwitterUrlRequest
  );
  yield takeLatest(
    TWITTER_CALLBACK_URL_STATUS_SUCCESS,
    handleTwitterCallbackUrlStatusSuccess
  );
}
